import { useEffect, useRef, useState } from "react";

/**
 * Tracks whether a scrollable element has more content below its visible
 * area. Returns a ref to attach to the scroll container plus a `showFade`
 * flag — true when the user is not at the bottom and the content actually
 * overflows. DialogContent_ uses this to render a sticky bottom-fade
 * indicator.
 */
export function useScrollFade(): {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  showFade: boolean;
} {
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Capture as a non-nullable const so the inner closure inherits the
    // narrowing — TypeScript doesn't propagate the early-return narrow of
    // `el` across function boundaries.
    const target: HTMLDivElement = el;
    function check() {
      // Show fade when content overflows and we're not scrolled to the bottom (with 8px tolerance)
      const atBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 8;
      setShowFade(!atBottom && target.scrollHeight > target.clientHeight);
    }

    check();
    target.addEventListener("scroll", check, { passive: true });
    const observer = new ResizeObserver(check);
    observer.observe(target);
    return () => {
      target.removeEventListener("scroll", check);
      observer.disconnect();
    };
  }, []);

  return { scrollRef, showFade };
}
